/* ═══════════════════════════════════════════════════════════════
   Clients — Manage clients and organizations you work with
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useMemo } from 'react';
import { Plus, ChevronLeft, X, MapPin, Users, Briefcase } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import { SearchableSelect } from '../components/SearchableSelect';
import { Button } from '../components/ui/button';
import EmptyState from '../components/EmptyState';
import { OrgService } from '../services/orgService';
import { TeamService } from '../services/teamService';
import { ProjectService } from '../services/projectService';
import type { Organization, OrgAddress, OrgContact, TeamMember, Project } from '../../shared/types';
import { DataTable, Column, GroupOption } from '../components/DataTable';
import { listCached } from '../services/cloudCache';
import { hasPermission } from '../../shared/permissions';
import { isWeb } from '../lib/platform';
import { rem } from '../lib/rem';
import { toAddress } from '../routing/address';
import { useAddressSync } from '../routing/useAddressSync';
import type { AuthUser } from '../services/authService';
import { StatusBadge } from '../components/StatusBadge';
import { isArchived, activeOnly } from '../lib/archive';

const api = (window as any).api;

interface OrganizationsPageProps {
  toast: (msg: string, isError?: boolean) => void;
  onNavigate?: (view: string, context?: any) => void;
  authUser?: AuthUser | null;
}

const ORG_TYPES = [
  { value: 'client', label: 'Client' },
  { value: 'owner', label: 'Owner' },
  { value: 'both', label: 'Both' },
  { value: 'other', label: 'Other' },
];

const DEFAULT_INDUSTRIES = [
  'K-12 Education',
  'Higher Education',
  'Healthcare / Medical',
  'Government / Municipal',
  'Commercial / Office',
  'Retail',
  'Industrial / Manufacturing',
  'Hospitality',
  'Residential / Multi-Family',
  'Data Centers',
  'Transportation',
  'Energy / Utilities',
  'Religious / Houses of Worship',
  'Recreation / Sports',
  'Mixed Use',
  'Other',
];

// ─── Helpers ───

function getOrgCity(o: Organization) {
  return (o.addresses || [])[0]?.city || '';
}
function getOrgState(o: Organization) {
  return (o.addresses || [])[0]?.state || '';
}
function getPrimaryContact(o: Organization): OrgContact | undefined {
  const contacts = o.contacts || [];
  return contacts.find((c) => c.primary) || contacts[0];
}
function getPrimaryAddress(o: Organization): OrgAddress | undefined {
  const addresses = o.addresses || [];
  return addresses.find((a) => a.primary) || addresses[0];
}

// Semantic, theme-aware type badges. Tints are derived from the palette
// via color-mix so they track light/dark mode and never hardcode hex.
function typeBadge(t?: string) {
  if (t === 'client')
    return {
      bg: 'color-mix(in srgb, var(--ac) 12%, transparent)',
      color: 'var(--ac)',
      border: 'color-mix(in srgb, var(--ac) 22%, transparent)',
      text: 'Client',
    };
  if (t === 'owner')
    return {
      bg: 'color-mix(in srgb, var(--ok) 14%, transparent)',
      color: 'var(--ok)',
      border: 'color-mix(in srgb, var(--ok) 26%, transparent)',
      text: 'Owner',
    };
  if (t === 'both')
    return {
      bg: 'color-mix(in srgb, var(--ok) 12%, transparent)',
      color: 'var(--ok)',
      border: 'color-mix(in srgb, var(--ok) 24%, transparent)',
      text: 'Client + Owner',
    };
  return { bg: 'var(--sa)', color: 'var(--tm)', border: 'var(--bd)', text: t || 'Other' };
}

function emptyOrg(): Organization {
  return {
    id: '',
    name: '',
    shortName: '',
    type: 'client',
    phone: '',
    email: '',
    website: '',
    addresses: [],
    contacts: [],
    rwbPoc: '',
    clientManagerId: '',
    industry: '',
    industries: [],
    defaultFormaId: '',
    notes: '',
    createdAt: '',
    updatedAt: '',
  };
}

function emptyAddress(): OrgAddress {
  return { label: '', address: '', city: '', state: '', zip: '' };
}
function emptyContact(): OrgContact {
  return { name: '', title: '', email: '', phone: '' };
}

// ─── Main Component ───

function OrganizationsPage({ toast, onNavigate, authUser }: OrganizationsPageProps) {
  // Permission flags: the server enforces these too, but hiding buttons
  // the user can't use keeps the UI honest.
  const perms = authUser?.permissions;
  const canCreate = hasPermission(perms, 'organizations.create');
  const canEdit = hasPermission(perms, 'organizations.edit');
  const canArchive = hasPermission(perms, 'organizations.archive');
  const [orgs, setOrgs] = useState<Organization[]>(() => listCached<Organization>('organizations'));
  const [editing, setEditing] = useState<Organization | null>(null);
  const [dirty, setDirty] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showClosedProjects, setShowClosedProjects] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  async function loadData(retry = true) {
    try {
      setOrgs(await OrgService.list());
      try {
        setTeamMembers(await TeamService.list());
      } catch {}
      try {
        setProjects(await ProjectService.list());
      } catch {}
    } catch (e) {
      console.error('OrganizationsPage loadData failed:', e);
      if (retry) setTimeout(() => loadData(false), 1500);
    }
  }

  function startNew() {
    setEditing(emptyOrg());
    setDirty(true);
  }
  function update(updates: Partial<Organization>) {
    setEditing((prev) => (prev ? { ...prev, ...updates } : prev));
    setDirty(true);
  }

  async function save() {
    if (!editing) return;
    if (!editing.name.trim()) {
      toast('Enter a client name', true);
      return;
    }
    // Auto-set primary if only one contact/address
    const addresses = (editing.addresses || []).map((a, i, arr) => ({
      ...a,
      primary: arr.length === 1 ? true : a.primary,
    }));
    const contacts = (editing.contacts || []).map((c, i, arr) => ({
      ...c,
      primary: arr.length === 1 ? true : c.primary,
    }));
    try {
      await OrgService.save({ ...editing, addresses, contacts });
      await loadData();
      setEditing(null);
      setDirty(false);
      toast('Client saved');
    } catch (err: any) {
      toast('Save failed: ' + (err.message || err), true);
    }
  }

  async function bulkDeleteOrgs(items: Organization[]) {
    for (const o of items) {
      await OrgService.delete(o.id);
    }
    await loadData();
    toast(`Deleted ${items.length} client${items.length !== 1 ? 's' : ''}`);
  }

  async function doArchiveOrgs(items: Organization[], archived: boolean) {
    try {
      for (const o of items) {
        await OrgService.setArchived(o.id, archived, authUser?.displayName);
      }
      await loadData();
      toast(
        `${archived ? 'Archived' : 'Restored'} ${items.length} client${items.length !== 1 ? 's' : ''}`
      );
    } catch (err: any) {
      toast(
        `Those clients could not be ${archived ? 'archived' : 'restored'}: ${err.message || 'Unknown error'}`,
        true
      );
    }
  }

  // ─── All known industries (defaults + custom from data) ───
  const allIndustries = useMemo(() => {
    const custom = orgs.flatMap((o) =>
      o.industries?.length ? o.industries : o.industry ? [o.industry] : []
    );
    const merged = new Set([...DEFAULT_INDUSTRIES, ...custom]);
    return Array.from(merged).sort();
  }, [orgs]);

  // ─── Paste handler ───
  async function handlePaste(rows: Record<string, string>[]) {
    let created = 0;
    for (const row of rows) {
      if (!row.name) continue;
      const org: Organization = {
        ...emptyOrg(),
        name: row.name,
        type: (row.type as any) || 'client',
        phone: row.phone || '',
        email: row.email || '',
        industry: row.industry || '',
        industries: row.industry
          ? row.industry
              .split(/[;,]/)
              .map((s: string) => s.trim())
              .filter(Boolean)
          : [],
      };
      try {
        await OrgService.save(org);
        created++;
      } catch {
        /* skip bad rows */
      }
    }
    if (created > 0) {
      await loadData();
      toast(`Imported ${created} client${created !== 1 ? 's' : ''} from clipboard`);
    }
  }

  // ─── Column definitions ───
  const columns = useMemo(
    (): Column<Organization>[] => [
      {
        key: 'name',
        label: 'Name',
        width: 220,
        minWidth: 120,
        getValue: (o) => o.name,
        render: (o) => <span style={{ fontWeight: 600 }}>{o.name}</span>,
      },
      {
        key: 'type',
        label: 'Type',
        width: 120,
        minWidth: 80,
        filterOptions: ORG_TYPES,
        getValue: (o) => o.type || '',
        render: (o) => {
          const tb = typeBadge(o.type);
          return (
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                padding: '2px var(--space-2)',
                borderRadius: 'var(--radius-md)',
                background: tb.bg,
                color: tb.color,
                border: `1px solid ${tb.border}`,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {tb.text}
            </span>
          );
        },
      },
      {
        key: 'industries',
        label: 'Industries',
        width: 180,
        minWidth: 100,
        getValue: (o) =>
          (o.industries?.length ? o.industries : o.industry ? [o.industry] : []).join(', '),
        render: (o) => {
          const inds = o.industries?.length ? o.industries : o.industry ? [o.industry] : [];
          if (inds.length === 0) return <span style={{ color: 'var(--tm)' }}>{'–'}</span>;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
              {inds.map((ind) => (
                <span
                  key={ind}
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    padding: '1px var(--space-2)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--sa)',
                    color: 'var(--tx)',
                    border: '1px solid var(--bd)',
                    fontWeight: 500,
                  }}
                >
                  {ind}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: 'clientManagerId',
        label: 'Internal Client Manager',
        width: 160,
        minWidth: 100,
        filterOptions: [
          { value: '', label: '(None)' },
          ...teamMembers.filter((m) => m.active).map((m) => ({ value: m.id, label: m.name })),
        ],
        getValue: (o) => o.clientManagerId || '',
        render: (o) => {
          const m = teamMembers.find((tm) => tm.id === o.clientManagerId);
          return m ? <span>{m.name}</span> : <span style={{ color: 'var(--tm)' }}>{'–'}</span>;
        },
      },
      {
        key: 'contacts',
        label: 'Contacts',
        width: 160,
        minWidth: 80,
        getValue: (o) => String((o.contacts || []).length),
        render: (o) => {
          const primary = getPrimaryContact(o);
          const count = (o.contacts || []).length;
          if (!primary) return <span style={{ color: 'var(--tm)' }}>{'–'}</span>;
          return (
            <span>
              {primary.name}
              {count > 1 ? <span style={{ color: 'var(--tm)' }}> +{count - 1}</span> : null}
            </span>
          );
        },
      },
      {
        key: 'city',
        label: 'City',
        width: 130,
        minWidth: 60,
        getValue: (o) => getPrimaryAddress(o)?.city || '',
        render: (o) =>
          getPrimaryAddress(o)?.city || <span style={{ color: 'var(--tm)' }}>{'–'}</span>,
      },
      {
        key: 'state',
        label: 'State',
        width: 80,
        minWidth: 50,
        getValue: (o) => getPrimaryAddress(o)?.state || '',
        render: (o) =>
          getPrimaryAddress(o)?.state || <span style={{ color: 'var(--tm)' }}>{'–'}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 90,
        minWidth: 70,
        getValue: (o) => (isArchived(o) ? 'archived' : ''),
        render: (o) => (isArchived(o) ? <StatusBadge status="archived" /> : null),
      },
    ],
    [teamMembers]
  );

  // ─── Group options ───
  const groupOptions = useMemo(
    (): GroupOption<Organization>[] => [
      {
        key: 'type',
        label: 'By Type',
        getValue: (o) => {
          const t = o.type || 'other';
          return t.charAt(0).toUpperCase() + t.slice(1);
        },
      },
      {
        // Keyed to the 'industries' COLUMN (DataTable merges by key) — this
        // used to be keyed 'industry' (singular), which matched no column
        // and was silently dropped from the group menu.
        key: 'industries',
        label: 'By Industry',
        getValue: (o) => {
          const inds = o.industries?.length ? o.industries : o.industry ? [o.industry] : [];
          return inds[0] || 'No Industry';
        },
      },
      {
        key: 'state',
        label: 'By State',
        getValue: (o) => getPrimaryAddress(o)?.state || 'No State',
      },
      {
        key: 'city',
        label: 'By City',
        getValue: (o) => getPrimaryAddress(o)?.city || 'No City',
      },
      {
        key: 'clientManagerId',
        label: 'By Client Manager',
        getValue: (o) => {
          const m = teamMembers.find((tm) => tm.id === o.clientManagerId);
          return m ? m.name : 'Unassigned';
        },
      },
    ],
    [teamMembers]
  );

  // ─── Projects for this client ───
  const clientProjects = useMemo(() => {
    if (!editing?.id) return [];
    const all = projects.filter((p) => p.clientOrgId === editing.id);
    return showClosedProjects ? all : all.filter((p) => p.status === 'active');
  }, [editing?.id, projects, showClosedProjects]);

  const clientProjectsTotal = useMemo(() => {
    if (!editing?.id) return 0;
    return projects.filter((p) => p.clientOrgId === editing.id).length;
  }, [editing?.id, projects]);

  // Address: the client open in the editor; Back from it returns to the list.
  // `/clients/<id>` has no deep-link consumer, so the page opens it itself.
  const [pendingClientId, setPendingClientId] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingClientId || orgs.length === 0) return;
    const target = orgs.find((o) => o.id === pendingClientId);
    if (target) {
      setEditing({ ...target });
      setDirty(false);
    }
    setPendingClientId(null);
  }, [pendingClientId, orgs]);
  useAddressSync({
    address: editing?.id ? toAddress({ kind: 'client', clientId: editing.id }) : null,
    listPath: '/clients',
    onLeave: () => {
      setEditing(null);
      setDirty(false);
    },
    onOpenId: setPendingClientId,
    guard: {
      when: dirty,
      onBlocked: (proceed, reset) => {
        if (confirm('Discard unsaved changes?')) proceed();
        else reset();
      },
    },
    title: editing?.id ? editing.name || null : null,
  });

  // ─── List View ───
  if (!editing) {
    return (
      <div
        style={{
          padding: 'var(--space-5)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-4)',
            flexShrink: 0,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
                margin: 0,
                letterSpacing: '-0.01em',
              }}
            >
              Clients
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--tm)',
                margin: 'var(--space-1) 0 0',
              }}
            >
              {(() => {
                const n = activeOnly(orgs, showArchived).length;
                return `${n} client${n !== 1 ? 's' : ''}`;
              })()}{' '}
              &middot; Click a row to open it
            </p>
          </div>
        </div>

        <DataTable<Organization>
          columns={columns}
          data={activeOnly(orgs, showArchived)}
          keyField="id"
          storageKey="clients"
          onRowClick={(o) => {
            setEditing({ ...o });
            setDirty(false);
          }}
          rowHref={(o) => toAddress({ kind: 'client', clientId: o.id })}
          groupOptions={groupOptions}
          emptyIcon={'○'}
          emptyTitle="No Clients Yet"
          emptyMessage="Add the clients and firms you work with."
          permissions={perms}
          bulkActions={{
            delete: { permission: 'organizations.delete', onDelete: bulkDeleteOrgs },
            archive: {
              permission: 'organizations.archive',
              onArchive: (items) => doArchiveOrgs(items, true),
              onUndo: (items) => doArchiveOrgs(items, false),
              label: 'Archive',
            },
            exportCsv: { permission: 'organizations.export' },
            extra: (items, meta) => {
              const archivedOnes = items.filter((o) => isArchived(o));
              if (!archivedOnes.length || !canArchive) return null;
              return (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void doArchiveOrgs(archivedOnes, false);
                    meta?.clear();
                  }}
                >
                  Restore {archivedOnes.length}
                </Button>
              );
            },
          }}
          toolbarExtra={
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button
                variant={showArchived ? 'secondary' : 'outline'}
                size="sm"
                aria-pressed={showArchived}
                onClick={() => setShowArchived((v) => !v)}
              >
                {showArchived ? 'Hide archived' : 'Show archived'}
              </Button>
              {canCreate && (
                <Button onClick={startNew}>
                  <Icon icon={Plus} em={1.2727} />
                  New Client
                </Button>
              )}
            </div>
          }
        />
      </div>
    );
  }

  // ─── Helpers to update arrays ───
  // These helpers are only called from the editor view, which is guarded by
  // an `if (!editing) return null` at the top — so `editing` is non-null
  // here. The non-null assertion keeps strictNullChecks happy without
  // adding redundant runtime branches.
  function updateAddr(i: number, patch: Partial<OrgAddress>) {
    const addresses = [...(editing!.addresses || [])];
    addresses[i] = { ...addresses[i], ...patch };
    update({ addresses });
  }
  function updateContact(i: number, patch: Partial<OrgContact>) {
    const contacts = [...(editing!.contacts || [])];
    contacts[i] = { ...contacts[i], ...patch };
    update({ contacts });
  }
  function setPrimaryAddress(index: number) {
    const addresses = (editing!.addresses || []).map((a, i) => ({ ...a, primary: i === index }));
    update({ addresses });
  }
  function setPrimaryContact(index: number) {
    const contacts = (editing!.contacts || []).map((c, i) => ({ ...c, primary: i === index }));
    update({ contacts });
  }

  // ─── Editor View ───
  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-5)' }}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (dirty && !confirm('Discard unsaved changes?')) return;
            setEditing(null);
            setDirty(false);
          }}
          style={{ marginBottom: 'var(--space-4)' }}
        >
          <Icon icon={ChevronLeft} em={1.4} />
          Back
        </Button>

        <div style={{ display: 'flex', gap: 'var(--space-5)' }}>
          {/* LEFT — Client info */}
          <div style={{ flex: 1, maxWidth: rem(440) }}>
            <h3
              style={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 700,
                marginBottom: 'var(--space-3)',
              }}
            >
              Client Info
            </h3>
            <Field label="Client Name" value={editing.name} onChange={(v) => update({ name: v })} />
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Type
              </div>
              <SearchableSelect
                value={editing.type || 'other'}
                options={ORG_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                onChange={(v) => update({ type: v as any })}
                clearable={false}
                searchPlaceholder="Search types…"
              />
            </div>
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Industries
              </div>
              <IndustryTagEditor
                org={editing}
                allIndustries={allIndustries}
                onSave={(industries) => update({ industries, industry: industries[0] || '' })}
              />
            </div>
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Internal Client Manager
              </div>
              <SearchableSelect
                value={editing.clientManagerId || ''}
                options={teamMembers
                  .filter((m) => m.active)
                  .map((m) => ({ value: m.id, label: m.name }))}
                onChange={(v) => update({ clientManagerId: v })}
                placeholder="(None)"
                searchPlaceholder="Search team…"
              />
            </div>
            <Field
              label="Phone"
              value={editing.phone || ''}
              onChange={(v) => update({ phone: v })}
            />
            <Field
              label="Email"
              value={editing.email || ''}
              onChange={(v) => update({ email: v })}
            />
            <Field
              label="Website"
              value={editing.website || ''}
              onChange={(v) => update({ website: v })}
            />
            {/* File Storage Directory — desktop only: browses/opens a local path. */}
            {!isWeb && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--tm)',
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  File Storage Directory
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <input
                    value={editing.fileStoragePath || ''}
                    onChange={(e) => update({ fileStoragePath: e.target.value })}
                    placeholder="Path to client files folder..."
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const result = await api.showOpenDialog({ properties: ['openDirectory'] });
                        if (result && result.filePaths && result.filePaths[0]) {
                          update({ fileStoragePath: result.filePaths[0] });
                        }
                      } catch {}
                    }}
                  >
                    Browse...
                  </Button>
                </div>
                {editing.fileStoragePath && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-primary"
                    onClick={() => {
                      try {
                        api.openPath(editing.fileStoragePath);
                      } catch {}
                    }}
                    style={{ marginTop: 'var(--space-1)' }}
                  >
                    Open in Explorer
                  </Button>
                )}
              </div>
            )}
            <h3
              style={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 700,
                marginTop: 'var(--space-5)',
                marginBottom: 'var(--space-2)',
              }}
            >
              Notes
            </h3>
            <textarea
              value={editing.notes || ''}
              onChange={(e) => update({ notes: e.target.value })}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* RIGHT — Projects table */}
          {editing.id && (
            <div style={{ flex: 1, maxWidth: rem(500) }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 'var(--space-3)',
                }}
              >
                <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, margin: 0 }}>
                  Projects ({clientProjects.length}
                  {showClosedProjects ? '' : ` of ${clientProjectsTotal}`})
                </h3>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--tm)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showClosedProjects}
                    onChange={(e) => setShowClosedProjects(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Show closed
                </label>
              </div>
              {clientProjects.length === 0 ? (
                <EmptyState
                  icon={<Icon icon={Briefcase} em={2.1538} />}
                  title={
                    showClosedProjects
                      ? 'No projects linked to this client.'
                      : 'No active projects.'
                  }
                  subtitle={showClosedProjects ? undefined : 'Toggle "Show closed" to see history.'}
                />
              ) : (
                <div
                  style={{
                    background: 'var(--sf)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--bd)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '90px 1fr 70px',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      borderBottom: '1px solid var(--bd)',
                      background: 'var(--sa)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 700,
                        color: 'var(--tm)',
                        textTransform: 'uppercase',
                      }}
                    >
                      Project #
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 700,
                        color: 'var(--tm)',
                        textTransform: 'uppercase',
                      }}
                    >
                      Name
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 700,
                        color: 'var(--tm)',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                      }}
                    >
                      Status
                    </span>
                  </div>
                  <div style={{ maxHeight: rem(400), overflowY: 'auto' }}>
                    {clientProjects.map((p, i) => (
                      <div
                        key={p.id}
                        onClick={() => onNavigate?.('projects', { projectId: p.id })}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '90px 1fr 70px',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-3)',
                          borderBottom:
                            i < clientProjects.length - 1 ? '1px solid var(--bd)' : 'none',
                          alignItems: 'center',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-base)',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = 'rgba(var(--acr),0.05)')
                        }
                        onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                      >
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            color: 'var(--ac)',
                            fontWeight: 600,
                            fontSize: 'var(--font-size-sm)',
                          }}
                        >
                          {p.projectNumber || '–'}
                        </span>
                        <span style={{ fontWeight: 500 }}>{p.name}</span>
                        <span
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            padding: '2px var(--space-2)',
                            borderRadius: 'var(--radius-full)',
                            textAlign: 'center',
                            background:
                              p.status === 'active'
                                ? 'color-mix(in srgb, var(--ok) 14%, transparent)'
                                : 'var(--sa)',
                            color: p.status === 'active' ? 'var(--ok)' : 'var(--tm)',
                            border: `1px solid ${
                              p.status === 'active'
                                ? 'color-mix(in srgb, var(--ok) 26%, transparent)'
                                : 'var(--bd)'
                            }`,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        >
                          {p.status || 'active'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Addresses */}
        <div style={{ marginTop: 'var(--space-5)', maxWidth: rem(900) }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--space-3)',
            }}
          >
            <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, margin: 0 }}>
              Addresses
            </h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => update({ addresses: [...(editing.addresses || []), emptyAddress()] })}
            >
              <Icon icon={Plus} em={1.4} />
              Add Address
            </Button>
          </div>
          {!editing.addresses || editing.addresses.length === 0 ? (
            <EmptyState icon={<Icon icon={MapPin} em={2.1538} />} title="No addresses yet." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {editing.addresses.map((addr, i) => (
                <div
                  key={i}
                  style={{
                    ...editorCardStyle,
                    borderColor: addr.primary ? 'var(--ac)' : 'var(--bd)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      marginBottom: 'var(--space-2)',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      value={addr.label || ''}
                      placeholder="Label (e.g. Main Office)"
                      onChange={(e) => updateAddr(i, { label: e.target.value })}
                      style={{
                        ...inputStyle,
                        flex: 1,
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 600,
                      }}
                    />
                    <Button
                      variant={addr.primary ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPrimaryAddress(i)}
                    >
                      {addr.primary ? 'Primary' : 'Set Primary'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-danger"
                      onClick={() =>
                        update({
                          addresses: (editing.addresses || []).filter((_, idx) => idx !== i),
                        })
                      }
                    >
                      Remove
                    </Button>
                  </div>
                  <input
                    value={addr.address}
                    placeholder="Street Address"
                    onChange={(e) => updateAddr(i, { address: e.target.value })}
                    style={{ ...inputStyle, marginBottom: 'var(--space-2)' }}
                  />
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      gap: 'var(--space-2)',
                    }}
                  >
                    <input
                      value={addr.city}
                      placeholder="City"
                      onChange={(e) => updateAddr(i, { city: e.target.value })}
                      style={inputStyle}
                    />
                    <input
                      value={addr.state}
                      placeholder="State"
                      onChange={(e) => updateAddr(i, { state: e.target.value })}
                      style={inputStyle}
                    />
                    <input
                      value={addr.zip}
                      placeholder="Zip"
                      onChange={(e) => updateAddr(i, { zip: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contacts */}
        <div style={{ marginTop: 'var(--space-5)', maxWidth: rem(900) }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--space-3)',
            }}
          >
            <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, margin: 0 }}>
              Contacts
            </h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => update({ contacts: [...(editing.contacts || []), emptyContact()] })}
            >
              <Icon icon={Plus} em={1.4} />
              Add Contact
            </Button>
          </div>
          {!editing.contacts || editing.contacts.length === 0 ? (
            <EmptyState icon={<Icon icon={Users} em={2.1538} />} title="No contacts yet." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {editing.contacts.map((c, i) => (
                <div
                  key={i}
                  style={{ ...editorCardStyle, borderColor: c.primary ? 'var(--ac)' : 'var(--bd)' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      marginBottom: 'var(--space-2)',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      value={c.name}
                      placeholder="Name"
                      onChange={(e) => updateContact(i, { name: e.target.value })}
                      style={{ ...inputStyle, flex: 1, fontWeight: 600 }}
                    />
                    <Button
                      variant={c.primary ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPrimaryContact(i)}
                    >
                      {c.primary ? 'Primary' : 'Set Primary'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-danger"
                      onClick={() =>
                        update({ contacts: (editing.contacts || []).filter((_, idx) => idx !== i) })
                      }
                    >
                      Remove
                    </Button>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 'var(--space-2)',
                    }}
                  >
                    <input
                      value={c.title || ''}
                      placeholder="Title / Role"
                      onChange={(e) => updateContact(i, { title: e.target.value })}
                      style={inputStyle}
                    />
                    <input
                      value={c.email || ''}
                      placeholder="Email"
                      onChange={(e) => updateContact(i, { email: e.target.value })}
                      style={inputStyle}
                    />
                    <input
                      value={c.phone || ''}
                      placeholder="Phone"
                      onChange={(e) => updateContact(i, { phone: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-5)',
            maxWidth: rem(900),
          }}
        >
          <Button onClick={save}>Save{dirty ? ' *' : ''}</Button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared Styles ───

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
};
const editorCardStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--bd)',
  padding: 'var(--space-3)',
};

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--tm)',
          marginBottom: 'var(--space-1)',
        }}
      >
        {label}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
      />
    </label>
  );
}

// ─── Industry Tag Editor — multi-select with type-to-add ───
function IndustryTagEditor({
  org,
  allIndustries,
  onSave,
}: {
  org: Organization;
  allIndustries: string[];
  onSave: (industries: string[]) => void;
}) {
  const current = org.industries?.length ? org.industries : org.industry ? [org.industry] : [];
  const [tags, setTags] = React.useState<string[]>(current);
  const [input, setInput] = React.useState('');
  const [open, setOpen] = React.useState(false);

  // Sync if org changes externally
  React.useEffect(() => {
    const next = org.industries?.length ? org.industries : org.industry ? [org.industry] : [];
    setTags(next);
  }, [org.id, org.industries?.join(','), org.industry]);

  function addTag(val: string) {
    const trimmed = val.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    const next = [...tags, trimmed];
    setTags(next);
    setInput('');
    onSave(next);
  }

  function removeTag(val: string) {
    const next = tags.filter((t) => t !== val);
    setTags(next);
    onSave(next);
  }

  const suggestions = allIndustries.filter(
    (i) => !tags.includes(i) && (!input || i.toLowerCase().includes(input.toLowerCase()))
  );

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-1)',
          padding: 'var(--space-1) var(--space-2)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--bd)',
          background: 'var(--ib)',
          minHeight: rem(28),
          alignItems: 'center',
        }}
      >
        {tags.map((t) => (
          <span
            key={t}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              fontSize: 'var(--font-size-sm)',
              padding: '2px var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--sa)',
              color: 'var(--tx)',
              fontWeight: 500,
            }}
          >
            {t}
            <button
              onClick={() => removeTag(t)}
              aria-label={`Remove ${t}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                color: 'var(--dg)',
                cursor: 'pointer',
                padding: rem(2),
                lineHeight: 1,
              }}
            >
              <Icon icon={X} em={0.9091} />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && input.trim()) {
              e.preventDefault();
              addTag(input);
            }
            if (e.key === 'Backspace' && !input && tags.length > 0)
              removeTag(tags[tags.length - 1]);
          }}
          placeholder={tags.length === 0 ? 'Add industries...' : ''}
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--tx)',
            fontSize: 'var(--font-size-base)',
            outline: 'none',
            flex: 1,
            minWidth: rem(80),
            padding: '2px 0',
          }}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            maxHeight: rem(150),
            overflow: 'auto',
            marginTop: rem(2),
          }}
        >
          {suggestions.slice(0, 15).map((s) => (
            <div
              key={s}
              onMouseDown={() => addTag(s)}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--font-size-base)',
                cursor: 'pointer',
                borderBottom: '1px solid var(--bd)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

(window as any).OrganizationsPage = OrganizationsPage;
export { OrganizationsPage };
